import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AntromLogo from '../../../assets/antrom-logo.svg?react';
import { NAVIGATION_ITEMS, PROFILE_ITEMS } from '../../../constants/navigation';
import NavigationDesktopMenu from './NavigationDesktopMenu/NavigationDesktopMenu';
import NavigationDesktopMenuItem from './NavigationDesktopMenuItem/NavigationDesktopMenuItem';
import NotificationButton from '../../notifications/NotificationButton/NotificationButton';
import NotificationDropdown from '../../notifications/NotificationDropdown/NotificationDropdown';
import NavigationMobileMenuButton from './NavigationMobileMenuButton/NavigationMobileMenuButton';
import NavigationMobileMenuWrapper from './NavigationMobileMenuWrapper/NavigationMobileMenuWrapper';
import NavigationMobileMenu from './NavigationMobileMenu/NavigationMobileMenu';
import NavigationMobileMenuItem from './NavigationMobileMenuItem/NavigationMobileMenuItem';
import { fetchUnreadNotificationCount, selectUnreadNotificationCount } from 'store/slices/notificationsSlice';

export default function Navigation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const unreadCount = useSelector(selectUnreadNotificationCount);
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuRef]);

  useEffect(() => {
    if (!user?._id) {
      return undefined;
    }

    dispatch(fetchUnreadNotificationCount());

    const intervalId = window.setInterval(() => {
      dispatch(fetchUnreadNotificationCount());
    }, 60000);

    const handleWindowFocus = () => {
      dispatch(fetchUnreadNotificationCount());
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [dispatch, user?._id]);

  const renderAvatar = (className = "size-8", textClassName = "text-xs") => {
    const first = user?.firstName ? user.firstName[0] : '';
    const last = user?.lastName ? user.lastName[0] : '';

    if (first && last) {
      return (
        <span className={`inline-flex ${className} items-center justify-center rounded-full bg-gray-800 ring-1 ring-white/10`}>
          <span className={`${textClassName} font-medium text-gray-400 leading-none`}>{(first + last).toUpperCase()}</span>
        </span>
      );
    }

    return (
      <span className={`${className} inline-block overflow-hidden rounded-full bg-gray-800 ring-1 ring-white/10`}>
        <svg className="size-full text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </span>
    );
  };

  return (
    <nav className="bg-gray-800/50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <div className="shrink-0">
              <AntromLogo  className="size-8" />
            </div>
            <NavigationDesktopMenu>
              {NAVIGATION_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return <NavigationDesktopMenuItem
                  key={item.id}
                  to={item.path}
                  text={item.title}
                  isActive={isActive}
                />;
              })}
            </NavigationDesktopMenu>
          </div>
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              <NotificationDropdown count={unreadCount} />

              <div className="relative ml-3" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="relative flex max-w-xs items-center rounded-full focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 cursor-pointer"
                >
                  <span className="absolute -inset-1.5"></span>
                  <span className="sr-only">Open user menu</span>
                  {renderAvatar("size-8")}
                </button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-gray-800 outline-1 -outline-offset-1 outline-white/10 py-1 shadow-lg">
                    {PROFILE_ITEMS.map((item) => (
                      <Link
                        key={item.id}
                        to={item.path}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <NavigationMobileMenuButton
            isOpen={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />

        </div>
      </div>

      <NavigationMobileMenuWrapper isOpen={isMobileMenuOpen}>
        <NavigationMobileMenu>
          {NAVIGATION_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return <NavigationMobileMenuItem
              key={item.id}
              to={item.path}
              text={item.title}
              isActive={isActive}
            />;
          })}
        </NavigationMobileMenu>
        <div className="border-t border-white/10 pt-4 pb-3">
          <div className="flex items-center px-5">
            <div className="shrink-0">
              {renderAvatar("size-10", "text-base")}
            </div>
            <div className="ml-3">
              <div className="text-base/5 font-medium text-white">
                {(!user?.firstName && !user?.lastName) ? 'Full Name' : `${user?.firstName || ''} ${user?.lastName || ''}`}
              </div>
              <div className="text-sm font-medium text-gray-400">{user?.email || ''}</div>
            </div>
            <NotificationButton
              className="ml-auto shrink-0"
              count={unreadCount}
              showDot={unreadCount > 0}
              hasPopup={false}
              onClick={() => navigate('/notifications')}
            />
          </div>
          <div className="mt-3 space-y-1 px-2">
            {PROFILE_ITEMS.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-white/5 hover:text-white"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </NavigationMobileMenuWrapper>
    </nav>
  );
}
